/**
 * MONSTER TOWN — ECONOMY INSTALL. OWNED BY THE VENUE/ECONOMY CREW.
 *
 * Wires the three coin taps that are not the venue (jobs, finds, daily gifts),
 * opens the town shop, seeds a brand new town with the things that make the
 * loop discoverable, and fills in the unlock ladder's `grants` from whatever
 * every crew has registered.
 *
 * ECONOMY DESIGN, RESTATED SO IT IS NOT DRIFTED AWAY FROM:
 *  - coins come EASILY. Two customers pay for a new flavour.
 *  - the ladder runs on LIFETIME EARNED, so buying never un-unlocks anything.
 *  - basics are FREE FOREVER, so a kid can never be stuck unable to build.
 *  - nothing is ever taken away, ever. There are no fees and no upkeep.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { allPlaceables } from '../core/placeable'
import { setUnlockLadder, unlockLadder, wallet } from '../core/economy'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import type { SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { place } from '../build/placer'
import { allVenues } from '../venue/types'
import { registerRoom } from '../venue/scene'
import { JOB_BOARD_CELL, TOWN_SHOP_SCENE, jobBoard, shopCounter, venueExit } from '../content/venue'
import { awardDailyGift, installJobs, updateFinds, updateMarks } from './jobs'
import { SHOP_COUNTER_CELL, SHOP_DOOR_CELL, installShop, restockShop, updateJiggles } from './shop'

const TOWN = 'town' as SceneId

interface EconSave {
  seeded: boolean
  lastSeenAt: number
}

let seeded = false
let lastSeenAt = 0

export function economySeeded(): boolean {
  return seeded
}

// ---------------------------------------------------------------------------
// seeding — a brand new town already has somewhere to go and something to do
// ---------------------------------------------------------------------------

function hasDef(sceneId: SceneId, defId: string): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  return node.grid.all().some((p) => p.defId === defId)
}

/**
 * A brand new town gets a job board, and nothing else from this crew. The
 * world crew's buildings already carry the front doors into the venues and the
 * shop (`toScene`), so seeding our own shopfront placeables would drop a
 * duplicate shop in the middle of the kid's buildable plot. The shopfront
 * placeables stay in the catalogue for a kid who wants to build their own.
 */
function seedTown(): void {
  if (!hasDef(TOWN, jobBoard.id)) {
    place(TOWN, jobBoard.id, JOB_BOARD_CELL, 0, { silent: true })
  }
}

/** Open every venue with its starter kit so it is playable the first time in. */
function seedVenues(): void {
  for (const def of allVenues()) {
    const node = world.get(def.sceneId)
    if (node === undefined || node.grid.count() > 0) continue
    for (const item of def.starter) {
      place(def.sceneId, item.defId, item.anchor, item.rot ?? 0, { silent: true })
    }
  }
  // The shop is not a venue but it still needs a way back out — and a counter
  // to be served at, so it reads as a shop rather than a room full of stock.
  const shopNode = world.get(TOWN_SHOP_SCENE)
  if (shopNode !== undefined && shopNode.grid.at(SHOP_DOOR_CELL) === null) {
    place(TOWN_SHOP_SCENE, venueExit.id, SHOP_DOOR_CELL, 0, { silent: true })
  }
  if (shopNode !== undefined && shopNode.grid.at(SHOP_COUNTER_CELL) === null) {
    place(TOWN_SHOP_SCENE, shopCounter.id, SHOP_COUNTER_CELL, 0, { silent: true })
  }
}

export function seedWorld(force = false): void {
  if (seeded && !force) return
  seeded = true
  seedTown()
  seedVenues()
  restockShop()
  save.request('econ:seed')
}

// ---------------------------------------------------------------------------
// unlock ladder — grants are DERIVED, so any crew's tiered content shows up
// ---------------------------------------------------------------------------

function fillLadderGrants(): void {
  const tiers = unlockLadder()
  if (tiers.length === 0) return
  setUnlockLadder(
    tiers.map((t) => ({
      ...t,
      grants: allPlaceables()
        .filter((d) => d.tier === t.id)
        .map((d) => d.id),
    })),
  )
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installEconomy(_ctx: GameContext): void {
  registerSaveSection<EconSave>(
    {
      id: 'econ',
      version: 1,
      defaults: () => ({ seeded: false, lastSeenAt: 0 }),
      serialize: () => ({ seeded, lastSeenAt: Date.now() }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        return {
          seeded: c.bool(raw['seeded'], false),
          lastSeenAt: Math.max(0, c.num(raw['lastSeenAt'], 0)),
        }
      },
    },
    (v) => {
      seeded = v.seeded
      lastSeenAt = v.lastSeenAt
    },
  )

  // The town shop's room, plus every venue's room, must exist before any save
  // section tries to put placements back into them.
  installShop()
  for (const def of allVenues()) {
    registerRoom({ sceneId: def.sceneId, devName: def.devName, grid: def.grid, shell: def.shell })
  }
  installJobs()

  bus.on('save:loaded', () => {
    fillLadderGrants()
    seedWorld()
    restockShop()
    const gift = awardDailyGift(lastSeenAt, Date.now())
    lastSeenAt = Date.now()
    if (gift > 0) save.request('econ:daily')
  })

  // A kid who has never earned anything still gets a handful to start with, so
  // the first trip to the shop is not a disappointment.
  bus.on('economy:unlocked', (e) => {
    bus.emit('ui:toast', { icon: 'star', ms: 2200 })
    bus.emit('audio:cue', { cue: 'unlock' })
    if (e.grants.length > 0) restockShop()
  })

  // SAVES ARE SACRED — the wallet saves ITSELF, not because a call site
  // remembered to ask.
  //
  // Coins and the owned catalogue used to persist only because individual
  // callers happened to call `save.request()` after `wallet.buy()` / `earn()`.
  // `buyFromPlinth` did; nothing else had to. A purchase made through any other
  // path (the harness buy, an unlock grant, a future shop panel) spent the coins
  // on screen and then handed them straight back on the next reload — a kid
  // saves up, buys the lamp, closes the iPad, and the lamp never happened.
  //
  // `wallet` is core and frozen, but it already emits on every mutation, so the
  // invariant can be closed additively and permanently right here: every future
  // caller is covered by construction and none of them can forget.
  bus.on('economy:spent', (e) => save.request(`wallet:spent:${e.on}`))
  bus.on('economy:earned', (e) => save.request(`wallet:earned:${e.source}`))
  bus.on('economy:unlocked', (e) => save.request(`wallet:unlocked:${e.tier}`))

  engine.addSystem({
    id: 'economy.town',
    order: 55,
    fixed: (dt) => {
      updateMarks(dt)
      updateFinds(dt)
      updateJiggles(dt)
    },
  })
}

/** Harness/debug: hand out coins without pretending they came from play. */
export function debugGrantCoins(n: number): number {
  return wallet.earnExact(n, 'debug')
}
